import { motion } from 'motion/react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../../utils/cn';

import { Button } from '../button';

/**
 * Props for {@link DataTableExpandableCellContent}.
 */
interface DataTableExpandableCellContentProps {
  /** Content to render inside the expandable container. */
  children: ReactNode;
  /** Optional className for the wrapper that contains content and toggle button. */
  className?: string;
  /**
   * Max height applied while collapsed.
   * Accepts CSS size values like `80`, `'5rem'`, `'120px'`, etc.
   */
  maxHeight?: number | string;
  /**
   * Optional max width for the content container.
   * Accepts CSS size values like `480`, `'30rem'`, `'100%'`, etc.
   */
  maxWidth?: number | string;
}

/**
 * Expandable wrapper for table-cell content.
 *
 * It truncates content by `maxHeight` when collapsed and shows a
 * `Ver más` / `Ver menos` toggle only when overflow exists.
 *
 * @example
 * ```tsx
 * <DataTableExpandableCellContent maxHeight="4rem" maxWidth="30rem">
 *   {String(row.getValue(attribute.id) ?? '')}
 * </DataTableExpandableCellContent>
 * ```
 */
export function DataTableExpandableCellContent({
  children,
  className,
  maxHeight = '4rem',
  maxWidth = '30rem',
}: DataTableExpandableCellContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const calculateCanExpand = useCallback(() => {
    const contentElement = contentRef.current;

    if (!contentElement) {
      setCanExpand(false);
      return;
    }

    const previousHeight = contentElement.style.height;
    const collapsedHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

    contentElement.style.height = collapsedHeight;
    const shouldExpand = contentElement.scrollHeight > contentElement.clientHeight;
    contentElement.style.height = previousHeight;

    setCanExpand(shouldExpand);
  }, [maxHeight]);

  useEffect(() => {
    calculateCanExpand();
  }, [children, maxWidth, calculateCanExpand]);

  useEffect(() => {
    const handleResize = () => {
      calculateCanExpand();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateCanExpand]);

  return (
    <div className={cn('space-y-1', className)}>
      <motion.div
        ref={contentRef}
        className={cn('min-w-[10rem] overflow-hidden break-words', {
          'flex items-center': !canExpand,
        })}
        style={{
          height: isExpanded ? 'auto' : maxHeight,
          maxWidth,
        }}
        animate={{
          height: isExpanded ? 'auto' : maxHeight,
        }}
        transition={{
          duration: 0.24,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        <div className="w-full">{children}</div>
      </motion.div>
      {!canExpand ? null : (
        <Button
          variant="link"
          size="xs"
          className="h-auto p-0"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
          }}
        >
          {isExpanded ? 'Ver menos' : 'Ver más'}
        </Button>
      )}
    </div>
  );
}
